/**
 * @file Region Feature - Main Entry Point
 * @description Main initialization and coordination for region component
 * @version 2.0.0 - Refactored for modularity and maintainability
 */

import { initializeDOMElements } from "./dom-manager.js";
import { initializeTippyManager } from "./tooltip-manager.js";
import { bindEventListeners } from "./event-manager.js";
import { setInitialized, isInitialized } from "./state.js";

/**
 * Main initialization function for region component
 */
export async function initRegion() {
  if (isInitialized()) {
    console.debug("Region component already initialized, skipping...");
    return;
  }

  try {
    console.debug("Initializing region component...");

    // Step 1: Initialize DOM elements cache
    initializeDOMElements();

    // Step 2: Initialize Tippy Manager for tooltips
    await initializeTippyManager();

    // Step 3: Bind event listeners
    await bindEventListeners();

    // Step 4: Mark as initialized
    setInitialized(true);

    console.debug("Region component initialized successfully");
  } catch (error) {
    console.error("Failed to initialize region component:", error);
    throw error;
  }
}

/**
 * Get region component status
 * @returns {Object} Component status
 */
export async function getRegionStatus() {
  const { getStateStats } = await import("./state.js");
  const { getCacheStats } = await import("./dom-manager.js");
  const { getAPIStats } = await import("./api-service.js");
  const { getTooltipStats } = await import("./tooltip-manager.js");

  return {
    initialized: isInitialized(),
    state: getStateStats(),
    dom: getCacheStats(),
    api: getAPIStats(),
    tooltips: getTooltipStats(),
  };
}

/**
 * Reset region component
 */
export async function resetRegion() {
  const { clearCache: clearStateCache } = await import("./state.js");
  const { clearCache: clearDOMCache } = await import("./dom-manager.js");
  const { unbindEventListeners } = await import("./event-manager.js");
  const { resetAllHighlighting } = await import("./highlight-manager.js");

  try {
    // Unbind event listeners
    unbindEventListeners();

    // Clear all caches
    clearStateCache();
    clearDOMCache();

    // Reset highlighting
    resetAllHighlighting();

    // Reset initialization status
    setInitialized(false);

    console.debug("Region component reset successfully");
  } catch (error) {
    console.error("Error resetting region component:", error);
  }
}

/**
 * Debug function for browser console access (development only)
 */
if (process.env.NODE_ENV === "development") {
  window.debugRegionSystem = function () {
    console.log("=== REGION SYSTEM DEBUG ===");
    console.log("Status:", getRegionStatus());
    console.log("=== END REGION SYSTEM DEBUG ===");
  };
}

// Export individual modules for advanced usage
export {
  // Core modules
  SELECTORS,
  ANIMATION,
  HIGHLIGHT,
  API_CONFIG,
  EVENTS,
} from "./constants.js";

export {
  // Utility functions
  findCorrespondingNavLink,
  getTermIDFromSlug,
  toggleLoading,
  hideElement,
  showElement,
  fetchData,
  findChildMap,
  generateTooltipContent,
} from "./utils.js";

export {
  // State management
  state,
  storeOriginalStyles,
  getCachedRegionData,
  setCachedRegionData,
  getOriginalRegionStyles,
  setTippyManager,
  getTippyManager,
  setEventManager,
  getEventManager,
  setInitialized,
  isInitialized,
  clearCache as clearStateCache,
  getStateStats,
} from "./state.js";

export {
  // DOM management
  initializeDOMElements,
  getElement,
  getAllElements,
  hasElement,
  getElementBySelector,
  getElementsBySelector,
  getElementById,
  createElement,
  updateElementContent,
  addClass,
  removeClass,
  toggleClass,
  setStyle,
  setStyles,
  getDataset,
  setDataset,
  clearCache as clearDOMCache,
  getCacheStats,
} from "./dom-manager.js";

export {
  // API service
  fetchRegionData,
  fetchProvinces,
  fetchRegionDetails,
  fetchMultipleRegions,
  searchRegions,
  fetchChildRegions,
  fetchAllRegions,
  preloadRegionData,
  clearAPICache,
  getAPIStats,
} from "./api-service.js";

export {
  // UI management
  updateProvinceListing,
  updateRegionDescription,
  showChildMap,
  hideAllChildMaps,
  showRegions,
  resetRegionDisplay,
  setProvinceListingLoading,
  setRegionDescriptionLoading,
  updateRegionSelect,
  showLoading,
  handleBackButton,
} from "./ui-manager.js";

export {
  // Highlight management
  highlightRegionOnMap,
  removeRegionHighlight,
  highlightRegionItem,
  removeRegionItemHighlight,
  setActiveRegionItem,
  removeActiveRegionItems,
  highlightMultipleRegions,
  highlightRegionWithCustomStyles,
  getRegionHighlightStatus,
  resetAllHighlighting,
} from "./highlight-manager.js";

export {
  // Tooltip management
  initializeTippyManager,
  handleRegionMouseOver,
  handleRegionMouseLeave,
  createTooltip,
  updateTooltipContent,
  showTooltip,
  hideTooltip,
  destroyTooltip,
  getTooltipInstance,
  hasTooltip,
  initTooltipsForSelector,
  getTooltipStats,
} from "./tooltip-manager.js";

export {
  // Event management
  initializeEventManager,
  handleZoneMouseOver,
  handleZoneMouseLeave,
  handleZoneClick,
  handleRegionClick,
  handleBackButtonClick,
  handleTurnBackButtonClick,
  handleRegionSelectChange,
  handleProvinceListingChange,
  handleProvinceMobileNavTabClick,
  bindEventListeners,
  unbindEventListeners,
} from "./event-manager.js";
